import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignatureBg } from "../components/SignatureBg";
import { PortalShell } from "../components/PortalShell";
import { portal } from "../lib/api";

export default function PortalLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await portal.login(email.trim(), password);
      nav("/portal/dashboard");
    } catch (e: any) {
      setError(e?.message || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  }

  // Forgot / never set a password → email a set-password link. Doubles as the
  // magic-link-free recovery path. Always reports success (no email enumeration).
  async function emailSetupLink() {
    if (!email.trim()) {
      setError("Enter your email first, then tap the link below.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await portal.requestPasswordSetup(email.trim());
      setNotice("If that email is on file, we just sent you a link to set your password. Check your inbox.");
    } catch (e: any) {
      setError(e?.message || "Couldn't send the link. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function emailMagicLink() {
    if (!email.trim()) {
      setError("Enter your email first, then tap the link below.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await portal.requestMagicLink(email.trim());
      nav("/portal/sent", { state: { email: email.trim() } });
    } catch (e: any) {
      setError(e?.message || "Couldn't send the sign-in link. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PortalShell narrow>
      <div className="portal-login has-signature-bg">
      <SignatureBg />
        <div className="portal-login-card">
          <p className="portal-eyebrow on-card" style={{ margin: 0 }}>Welcome</p>
          <h1>Sign in to your account</h1>
          <p className="portal-sub on-card">
            Enter the email and password you set up during intake. New here or
            forgot your password? Use the link below and we'll email you one.
          </p>
          <form onSubmit={submit} className="portal-section" style={{ gap: 14, marginTop: 6 }}>
            <label className="portal-section" style={{ gap: 6 }}>
              <span className="field-label">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-required="true"
                placeholder="you@example.com"
                required
              />
            </label>
            <label className="portal-section" style={{ gap: 6 }}>
              <span className="field-label">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-required="true"
                placeholder="Your password"
                required
              />
            </label>
            {error && <p className="portal-error">{error}</p>}
            {notice && <p className="portal-sub on-card" style={{ margin: 0 }}>{notice}</p>}
            <button className="portal-btn-primary wide" disabled={busy || !email || !password}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="portal-section" style={{ gap: 8, marginTop: 18, textAlign: "center" }}>
            <button type="button" className="portal-btn-link" onClick={emailSetupLink} disabled={busy}>
              Set or reset my password by email
            </button>
            <button type="button" className="portal-btn-link" onClick={emailMagicLink} disabled={busy}>
              Email me a one-time sign-in link instead
            </button>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
